import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import {
  ADMIN_DIRECT_LEAVE,
  ADMIN_EDIT_LEAVE,
  REFETCH_LEAVES,
  STATUS_LABELS,
  STATUS_TRANSITIONS,
  TIME_SLOTS,
  assertSuccess,
  leaveModeTimes,
  normalizeLeaveType,
} from "./leaveShared";

// "Mark Leave Directly" (no `leave`) and "Edit Leave" (with `leave`): the
// same form. A direct mark is APPROVED or ABSENT; an edit offers the
// statuses the leave can move to from where it is.

const blankForm = {
  employeeId: "",
  leaveType: "CASUAL",
  leaveMode: "FULL_DAY",
  startDate: "",
  endDate: "",
  startTime: "10:00:00",
  endTime: "19:00:00",
  reason: "",
  adminRemark: "",
  status: "APPROVED",
};

const formFromLeave = (leave) => ({
  employeeId: leave.employee?.id || "",
  leaveType: normalizeLeaveType(leave.leaveType),
  leaveMode: leave.leaveMode,
  startDate: leave.startDate,
  endDate: leave.endDate,
  startTime: leave.startTime || "10:00:00",
  endTime: leave.endTime || "19:00:00",
  reason: leave.reason || "",
  adminRemark: leave.adminRemark || "",
  status: leave.status,
});

const Label = ({ children }) => (
  <label className="block text-sm font-medium mb-1">{children}</label>
);

const LeaveFormModal = ({ leave, employees, leaveTypeOptions, onClose }) => {
  const isEdit = Boolean(leave);
  const [form, setForm] = useState(() => (isEdit ? formFromLeave(leave) : blankForm));
  const [saving, setSaving] = useState(false);
  const [markLeave] = useMutation(ADMIN_DIRECT_LEAVE, { refetchQueries: REFETCH_LEAVES });
  const [editLeave] = useMutation(ADMIN_EDIT_LEAVE, { refetchQueries: REFETCH_LEAVES });

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setMode = (mode) => setForm((f) => ({ ...f, leaveMode: mode, ...leaveModeTimes(mode) }));

  const statusOptions = isEdit
    ? [leave.status, ...(STATUS_TRANSITIONS[leave.status] || [])]
    : ["APPROVED", "ABSENT"];

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        const res = await editLeave({ variables: { data: { leaveId: leave.id, ...form } } });
        assertSuccess(res.data?.adminEditLeave);
      } else {
        const res = await markLeave({ variables: { data: form } });
        assertSuccess(res.data?.adminDirectMarkLeave);
      }
      onClose();
    } catch (err) {
      alert(`Error ${isEdit ? "updating" : "marking"} leave: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 w-1/3 min-w-[420px] max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">
          {isEdit ? "Edit Leave" : "Mark Leave Directly"}
        </h2>
        <form onSubmit={submit}>
          <div className="mb-4">
            <Label>Employee</Label>
            <select
              required
              className="w-full border p-2 rounded"
              value={form.employeeId}
              onChange={(e) => set("employeeId", e.target.value)}
            >
              <option value="" disabled>
                Select Employee
              </option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.fullName}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Leave Type</Label>
              <select
                required
                className="w-full border p-2 rounded"
                value={form.leaveType}
                onChange={(e) => set("leaveType", e.target.value)}
              >
                {leaveTypeOptions.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Leave Mode</Label>
              <select
                className="w-full border p-2 rounded"
                value={form.leaveMode}
                onChange={(e) => setMode(e.target.value)}
              >
                <option value="FULL_DAY">Full Day</option>
                <option value="HALF_DAY_FIRST">Half Day (First: 10am - 2pm)</option>
                <option value="HALF_DAY_SECOND">Half Day (Second: 2:30pm - 7pm)</option>
                <option value="CUSTOM_SLOT">Custom Slot</option>
              </select>
            </div>
          </div>

          {form.leaveMode === "CUSTOM_SLOT" && (
            <div className="grid grid-cols-2 gap-4 mb-4">
              {[
                ["startTime", "Start Time"],
                ["endTime", "End Time"],
              ].map(([key, label]) => (
                <div key={key}>
                  <Label>{label}</Label>
                  <select
                    className="w-full border p-2 rounded"
                    value={form[key]}
                    onChange={(e) => set(key, e.target.value)}
                  >
                    {TIME_SLOTS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 mb-4">
            {[
              ["startDate", "Start Date"],
              ["endDate", "End Date"],
            ].map(([key, label]) => (
              <div key={key}>
                <Label>{label}</Label>
                <input
                  required
                  type="date"
                  className="w-full border p-2 rounded"
                  value={form[key]}
                  onChange={(e) => set(key, e.target.value)}
                />
              </div>
            ))}
          </div>

          <div className="mb-4">
            <Label>Reason</Label>
            <textarea
              required
              className="w-full border p-2 rounded"
              value={form.reason}
              onChange={(e) => set("reason", e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Status</Label>
              <select
                className="w-full border p-2 rounded bg-white"
                value={form.status}
                onChange={(e) => set("status", e.target.value)}
              >
                {statusOptions.map((st) => (
                  <option key={st} value={st}>
                    {STATUS_LABELS[st] || st}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Admin Remark</Label>
              <input
                type="text"
                className="w-full border p-2 rounded"
                value={form.adminRemark}
                onChange={(e) => set("adminRemark", e.target.value)}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {isEdit ? "Save Changes" : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeaveFormModal;
